import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { AppointmentTypeGroup } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import { SortOrder } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/sort.pb';
import { Source } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/source_tenant.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useGetAppointmentColorsEnabled } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import {
  Chip,
  currency,
  DropdownField,
  EditIconSmall,
  SortingValue,
  Table,
  TableColumnConfig,
  TableRowActions,
  Text,
  useControlledField,
  useModalControl,
} from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { useDataSourceColumn } from '../../hooks/use-data-source-column';
import { PageConfig } from '../../types';
import { ColorDot } from '../appointment-panel/color-dot';
import { AppointmentTypesBulkColorAssignModal } from '../appointment-types-bulk-color-assign-modal';
import { ServiceTypesTable, ServiceTypesTableProps } from './ServiceTypesTable';

type AppointmentTypeGroupsTableProps = {
  locationId: string;
  pageConfig: PageConfig;
  serviceTypesData: AppointmentTypeGroup[] | undefined;
  totalCount: number;
  isLoading: boolean;
  isPaymentsEnabled?: boolean;
  debouncedSearchValue?: string;
  setPageConfig: Dispatch<SetStateAction<PageConfig>>;
  onEdit: ServiceTypesTableProps['onEdit'];
  onDelete: ServiceTypesTableProps['onDelete'];
  isMultiLocation: boolean;
  isIntegratedOffice: boolean;
  sourceDropdownProps: ReturnType<typeof useControlledField<'dropdown'>>;
};

export const AppointmentTypeGroupsTable = ({
  locationId,
  isLoading,
  serviceTypesData,
  pageConfig,
  totalCount,
  debouncedSearchValue,
  setPageConfig,
  onEdit,
  onDelete,
  isMultiLocation,
  isIntegratedOffice,
  sourceDropdownProps,
  isPaymentsEnabled = false,
}: AppointmentTypeGroupsTableProps) => {
  const { t } = useTranslation('scheduleServiceTypes');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { invalidateListAppointmentTypes, invalidateListMappings } =
    SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const { canUseAppointmentColors } = useGetAppointmentColorsEnabled();

  const { cellRenderer: sourceColumnCellRenderer, accessor: sourceColumnAccessor } = useDataSourceColumn({
    sourceIdField: 'sourceId',
  });

  const { data: sourcesData, isLoading: isLoadingSources } = SchedulerV3.Queries.useListSourceTenantsQuery({
    request: { locationId },
  });
  const sources = (sourcesData?.sources?.filter((s) => !!s.sourceTenantId) ?? []) as (Source & {
    sourceTenantId: string;
  })[];

  const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState<AppointmentType[]>([]);

  const handleTableRefetch = () => {
    invalidateListAppointmentTypes(locationId);
    invalidateListMappings(locationId);
  };

  const colorAssignModalControl = useModalControl();

  const tableColumns = useMemo(
    (): TableColumnConfig<AppointmentTypeGroup>[] => [
      {
        Header: t('Color'),
        id: 'appointmentTypeColor',
        omit: !canUseAppointmentColors,
        accessor: (row) => row,
        cellRenderer: (cellValue: AppointmentTypeGroup) => (
          <ColorDot
            size='large'
            color={cellValue?.appointmentType?.details?.clr || PRIMARY_EVENT_COLOR}
            {...(!isMobile ? { style: { margin: 'auto' } } : {})}
          />
        ),
        disableSortBy: true,
        maxWidth: 70,
      },
      {
        Header: isIntegratedOffice ? t('Appointment Type Name') : t('Service Name'),
        id: 'displayName',
        accessor: (row) => row?.appointmentType?.displayName || '-',
        minWidth: 150,
      },
      {
        Header: t('Source'),
        id: 'source',
        omit: !isIntegratedOffice,
        accessor: (row) =>
          row?.appointmentType?.sourceId ? sourceColumnAccessor({ sourceId: row.appointmentType.sourceId }) : undefined,
        cellRenderer: (cellValue) => {
          const sourceName = sourceColumnCellRenderer(cellValue);
          if (!sourceName) return <div css={styles.noSourceDash}>-</div>;
          return (
            <Chip variant='neutral' css={styles.sourceColumn}>
              <Icon name='data-sync-small' css={{ marginRight: theme.spacing(0.5) }} />
              {sourceName}
            </Chip>
          );
        },
        minWidth: 100,
        maxWidth: 250,
      },
      {
        Header: t('Duration'),
        id: 'durationMinutes',
        accessor: (row) =>
          row.appointmentType?.details?.dur ? `${row.appointmentType?.details.dur} ${t('Min')}` : '-',
        minWidth: 100,
        maxWidth: 150,
        disableSortBy: true,
      },
      {
        Header: t('Description'),
        id: 'description',
        omit: isIntegratedOffice,
        accessor: (row) => row.appointmentType?.description || '-',
        minWidth: 250,
        disableSortBy: true,
      },
      {
        Header: t('Pre-Pay Amount'),
        id: 'prepaymentAmount',
        disableSortBy: true,
        omit: isIntegratedOffice || !isPaymentsEnabled,
        accessor: (row) => row.bookableAppointmentTypes?.[0]?.prepaymentAmount || 0,
        cellRenderer: (amount) => (amount ? currency(amount, { decimals: 2 }) : '-'),
        minWidth: 100,
        maxWidth: 150,
        cellAlign: 'center',
      },
      {
        Header: t('Bookable Services'),
        id: 'bookableServices',
        omit: !isIntegratedOffice,
        accessor: (row) => row.bookableAppointmentTypes?.length || 0,
        cellRenderer: (value) => <ServiceCountChip value={value} />,
        minWidth: 250,
        disableSortBy: true,
      },
    ],
    [t, canUseAppointmentColors, sourceColumnAccessor, sourceColumnCellRenderer, isMobile, isIntegratedOffice]
  );

  const tableActionMenu: TableRowActions<AppointmentTypeGroup> = useMemo(() => {
    return {
      rowActionMenuLabel: isIntegratedOffice ? t('Actions') : undefined,
      actions: isIntegratedOffice
        ? [
            {
              Icon: () => <Icon name='add-small' />,
              label: t('Add New Service'),
              hide: (row) => !row.appointmentType?.details?.act,
              onClick: () => {},
            },
          ]
        : [
            {
              Icon: () => <Icon name='edit-small' />,
              label: t('Edit'),
              hide: (row) => !row.appointmentType?.details?.act,
              onClick: (row) =>
                onEdit({ appointmentTypeGroup: row, bookableAppointmentType: row.bookableAppointmentTypes?.[0] }),
              showLabelAlways: false,
            },
            {
              Icon: () => <Icon name='trash-small' color='error' />,
              label: t('Delete'),
              showLabelAlways: false,
              css: { span: { color: theme.colors.critical50 } },
              hide: (row) => !row.appointmentType?.details?.act,
              onClick: (row: AppointmentTypeGroup) =>
                onDelete({ appointmentTypeGroup: row, bookableAppointmentType: row.bookableAppointmentTypes?.[0] }),
            },
          ],
    };
  }, [onEdit, t, isIntegratedOffice, onDelete]);

  const bulkActions = useMemo(
    () => [
      {
        Icon: EditIconSmall,
        label: t('Assign Colors'),
        onClick: (_: any, selectedRows: AppointmentTypeGroup[]) => {
          const selectedAppointmentTypesArr = selectedRows.map((row) => row.appointmentType);
          if (setSelectedAppointmentTypes) {
            setSelectedAppointmentTypes(selectedAppointmentTypesArr);
          }
          colorAssignModalControl.openModal();
        },
      },
    ],
    [t, setSelectedAppointmentTypes, colorAssignModalControl]
  );

  const handleSorting = (sortValues: SortingValue<string>[]) => {
    const { id, value } = sortValues[0] || {};
    if (id && value) {
      setPageConfig((prev) => ({
        ...prev,
        sortBy: id === 'displayName' ? 'DisplayName' : undefined,
        sortDir: value === 'asc' ? SortOrder.ASC : SortOrder.DESC,
      }));
    }
  };

  return (
    <>
      <AppointmentTypesBulkColorAssignModal
        {...colorAssignModalControl.modalProps}
        isCalendarView={false}
        shouldShowLocationColumn={isMultiLocation}
        appointmentTypes={selectedAppointmentTypes}
        isEdit
      />

      <Table
        data={serviceTypesData || []}
        isLoading={isLoading}
        colConfig={tableColumns}
        tableStyle={{
          name: 'appointment-type-v3-table',
          styles: `margin-bottom: ${theme.spacing(4)};`,
        }}
        styleConfig={{
          rows: (row: AppointmentTypeGroup) => {
            if (row.appointmentType?.details?.act === null || !row.appointmentType?.details?.act) {
              return css`
                color: ${theme.font.colors.disabled};
                min-height: ${theme.spacing(7.2)};
              `;
            }
            return css`
              min-height: ${theme.spacing(7.2)};
            `;
          },
          columns: [
            {
              id: '*',
              cellStyler: () =>
                css`
                  &.expanded:not(:hover) {
                    background-color: inherit !important;
                  }
                `,
            },
          ],
        }}
        isPaginated
        hasResponsiveColWidths
        rowActions={tableActionMenu}
        bulkActions={bulkActions}
        isSelectable={canUseAppointmentColors && !isIntegratedOffice}
        {...(isIntegratedOffice
          ? {
              expandableRowComponent: (row) => (
                <ServiceTypesTable
                  appointmentTypeGroup={row}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isPaymentsEnabled={isPaymentsEnabled}
                />
              ),
              rowExpandingConfig: {
                isDisabled: (row) => !row?.bookableAppointmentTypes?.length,
              },
            }
          : {})}
        isRowDisabled={(row: AppointmentTypeGroup) => !row.appointmentType?.details?.act}
        rowSelectionConfig={{
          isRowDisabled(rowData) {
            return !rowData.appointmentType?.details?.act;
          },
          onSelectionToggle(_, data) {
            const selectedAppointmentTypesArr = data.map((row) => row.appointmentType);
            setSelectedAppointmentTypes(selectedAppointmentTypesArr);
          },
        }}
        hasGlobalSearch
        globalSearchConfig={{
          initialValue: '',
          placeholder: t('Search'),
          searchHandler: (searchValue) => {
            const trimmedSearchValue = searchValue.trim();
            if (totalCount && trimmedSearchValue.length !== 1) {
              setPageConfig((prev) => ({ ...prev, page: 1, searchValue: trimmedSearchValue }));
            } else if (searchValue.length === 0) {
              setPageConfig((prev) => ({ ...prev, searchValue: trimmedSearchValue }));
            }
          },
        }}
        tableActions={[
          {
            Icon: () => <Icon name='update' />,
            label: t('Refetch'),
            onClick: handleTableRefetch,
            type: 'icon',
          },
        ]}
        manualSortBy
        onSortChange={handleSorting}
        manualPaginationConfig={{
          showNumRowSelector: true,
          showGoToPageField: true,
          page: pageConfig.page || 1,
          defaultRowsPerPage: pageConfig.limit,
          rowsPerPageOptions: [10, 20, 30, 40, 50],
          hasNext: (pageConfig.page || 1) < Math.ceil((totalCount ?? 0) / pageConfig.limit),
          hasPrevious: pageConfig.page > 1,
          total: Math.ceil((totalCount ?? 0) / pageConfig.limit),
          onNumRowsChange: (pageSize) => {
            setPageConfig((prev) => ({ ...prev, limit: pageSize, page: 1 }));
          },
          handleChange: (pageNumber: number) => {
            setPageConfig((prev) => ({
              ...prev,
              page: pageNumber,
            }));
          },
        }}
        customToolbarRender={() => {
          if (!isIntegratedOffice) return null;
          return (
            <div css={{ marginRight: 'auto' }}>
              <DropdownField
                {...sourceDropdownProps}
                label={t('Source')}
                name='location'
                css={styles.sourceDropDown}
                startAdornment={<Icon name='data-sync-small' css={{ marginRight: theme.spacing(0.5) }} />}
                disabled={isLoading || isLoadingSources}
              >
                {sources.map((source) => (
                  <DropdownField.Option key={source.id} value={source.sourceTenantId}>
                    {source.sourceName}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            </div>
          );
        }}
        {...(!isLoading &&
          !debouncedSearchValue && {
            emptyStateConfig: {
              type: 'schedule_requests',
              header: '',
              description: () => (
                <div css={{ textAlign: 'center' }}>
                  <Text color='subdued'>{t('Ready to start scheduling?')}</Text>
                  <Text color='subdued'>{t('Set up your first appointment type to start taking bookings.')}</Text>
                </div>
              ),
            },
          })}
      />
    </>
  );
};

const ServiceCountChip = ({ value }: { value: number }) => {
  const { t } = useTranslation('scheduleServiceTypes');
  const hasServices = !!value;
  return (
    <Chip variant={hasServices ? 'primary' : 'warn'} css={styles.serviceCountChip}>
      {!hasServices && <Icon name='warning-badge-small' color='warn' />}
      <Text size='medium' css={{ color: 'inherit' }}>
        {t('Services')}: <b>{value}</b>
      </Text>
    </Chip>
  );
};

const styles = {
  sourceColumn: css`
    height: auto;
    max-width: 100%;
    & span {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
    }
  `,
  sourceDropDown: css`
    min-width: 150px;
  `,
  serviceCountChip: css`
    & span {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
    }
  `,
  noSourceDash: css`
    width: 100%;
    text-align: center;
  `,
};
